import { useState, type ChangeEvent, type FormEvent } from 'react';
import { toUserMessage } from '@platform/api-client';

import { Alert, FormField } from '@/shared/ui';
import { useOnboardingAccount } from '../../api/use-auth.mutation';
import { passwordField } from '../../schemas/auth.schemas';
import { consentItemsFrom, EMPTY_AGREEMENTS } from '../../utils/consent';
import type { AgreementValues } from '../../types/agreement-step.type';
import { AgreementsStep } from '../AgreementsStep';
import { PrimaryButton, StepHeading } from './OnboardingUi';

/**
 * 1단계 — 새 비밀번호 + 필수 약관 동의를 한 화면에서 받는다.
 * 비밀번호를 이미 바꾼 계정(이관 계정·재제출)은 동의만, 동의가 끝난 계정은 비밀번호만 보인다.
 */
export function AccountSetupStep({
  needsPassword,
  needsConsent,
}: {
  needsPassword: boolean;
  needsConsent: boolean;
}) {
  const [form, setForm] = useState({ newPassword: '', passwordConfirm: '' });
  const [agreements, setAgreements] =
    useState<AgreementValues>(EMPTY_AGREEMENTS);
  const [fieldErrors, setFieldErrors] = useState<{
    newPassword?: string;
    passwordConfirm?: string;
    consent?: string;
  }>({});
  const { mutate, isPending, error, reset } = useOnboardingAccount();

  const onChange =
    (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
      if (fieldErrors[key])
        setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
      if (error) reset();
    };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: typeof fieldErrors = {};
    if (needsPassword) {
      const parsed = passwordField.safeParse(form.newPassword);
      if (!parsed.success) errors.newPassword = parsed.error.issues[0]?.message;
      else if (form.newPassword !== form.passwordConfirm)
        errors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
    }
    if (needsConsent && (!agreements.agreeTerms || !agreements.agreePrivacy))
      errors.consent = '필수 약관에 모두 동의해 주세요.';
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    mutate({
      newPassword: needsPassword ? form.newPassword : undefined,
      consents: needsConsent ? consentItemsFrom(agreements) : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <StepHeading
        title={
          needsPassword ? '새 비밀번호를 정해 주세요' : '약관에 동의해 주세요'
        }
      >
        {needsPassword
          ? '발급받은 임시 비밀번호는 이후 사용할 수 없습니다. 다른 기기에 로그인되어 있었다면 그 기기는 로그아웃됩니다.'
          : '서비스 이용을 위해 필수 약관에 동의해 주세요.'}
      </StepHeading>

      {needsPassword && (
        <div className="space-y-4">
          <FormField
            label="새 비밀번호"
            type="password"
            name="newPassword"
            autoComplete="new-password"
            autoFocus
            required
            value={form.newPassword}
            onChange={onChange('newPassword')}
            error={fieldErrors.newPassword}
            hint="영문과 숫자를 포함해 8자 이상"
          />
          <FormField
            label="새 비밀번호 확인"
            type="password"
            name="passwordConfirm"
            autoComplete="new-password"
            required
            value={form.passwordConfirm}
            onChange={onChange('passwordConfirm')}
            error={fieldErrors.passwordConfirm}
          />
        </div>
      )}

      {needsConsent && (
        <div
          className={
            needsPassword
              ? 'mt-6 border-t border-neutral-200 pt-6 dark:border-white/10'
              : undefined
          }
        >
          {needsPassword && (
            <p className="mb-3 text-sm font-semibold text-neutral-950 dark:text-neutral-100">
              약관 동의
            </p>
          )}
          <AgreementsStep
            values={agreements}
            onChange={(next) => {
              setAgreements(next);
              setFieldErrors((prev) => ({ ...prev, consent: undefined }));
              if (error) reset();
            }}
            errors={
              fieldErrors.consent
                ? { agreeTerms: fieldErrors.consent }
                : undefined
            }
          />
        </div>
      )}

      {error && (
        <div className="mt-5">
          <Alert tone="error">{toUserMessage(error)}</Alert>
        </div>
      )}

      <PrimaryButton type="submit" disabled={isPending} className="mt-6">
        {isPending ? '저장 중…' : '다음'}
      </PrimaryButton>
    </form>
  );
}
